import React, { useCallback, useEffect, useState } from 'react'

import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { Alert } from '@material-ui/lab'
import { BasicEmptyState } from '@scoir/empty-state'
import { NonDismissibleDialog } from '@scoir/modals'
import { includes, map } from 'lodash'
import PropTypes from 'prop-types'

import api from 'common/api'

import RoleControl from './RoleControl'

const { Body, Footer } = NonDismissibleDialog
const { PrimaryActionButton, SecondaryActionButton } = Footer

const useStyles = makeStyles(() => ({
  root: {
    boxShadow: 'none',
  },
  alert: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
}))

const ManageRolesModal = ({
  userTarget,
  open,
  onClose,
  isUserPermitted,
  isReviewingNewUser,
  onDeleteUser,
  onInviteUser,
}) => {
  const [hasError, setHasError] = useState(false)
  const [hasChanged, setHasChanged] = useState(false)
  const [roles, setRoles] = useState(undefined)
  const [accountAdminAssigned, setAccountAdminAssigned] = useState(
    includes(userTarget.Roles, 'AdmissionsAdministrator'),
  )
  const [visitsManagerAssigned, setVisitsManagerAssigned] = useState(includes(userTarget.Roles, 'VisitsManager'))

  const classes = useStyles()

  const targetHasRole = (role) => includes(userTarget.Roles, role.Id)

  const fetchRoles = useCallback(() => {
    if (!roles && open) {
      api.get('/api/roles').then((roles) => setRoles(roles))
    }
  }, [open, roles])

  useEffect(() => fetchRoles(), [fetchRoles])
  useEffect(() => {
    if (open) {
      setHasChanged(false)
      setHasError(false)
      setAccountAdminAssigned(includes(userTarget.Roles, 'AdmissionsAdministrator'))
      setVisitsManagerAssigned(includes(userTarget.Roles, 'VisitsManager'))
    }
  }, [open, userTarget.Roles])

  const handleRoleChange = useCallback(
    (isAssigned, roleId) => {
      setHasChanged(true)
      setHasError(false)
      if (roleId === 'VisitsManager') {
        setVisitsManagerAssigned(isAssigned)
      }
      if (roleId === 'AdmissionsAdministrator') {
        setAccountAdminAssigned(isAssigned)
        if (isAssigned && !visitsManagerAssigned) {
          api
            .put(`/api/users/${userTarget.Id}/roles/VisitsManager`)
            .then(() => setVisitsManagerAssigned(true))
            .catch(() => setHasError(true))
        }
      }
    },
    [userTarget, visitsManagerAssigned],
  )

  return (
    <NonDismissibleDialog
      open={open}
      scroll="paper"
      onClose={() => onClose(false)}
      headerText={`Manage Roles for ${userTarget.FirstName} ${userTarget.LastName}`}
    >
      <Body>
        {!roles && (
          <Box
            p={8}
            textAlign="center"
          >
            <CircularProgress
              aria-label="Loading"
              role="status"
              size="100px"
            />
          </Box>
        )}
        {roles && roles.length === 0 && <BasicEmptyState title="No Roles Available" />}
        {roles &&
          roles.length > 0 &&
          roles.map((role) => (
            <Accordion
              classes={{
                root: classes.root,
              }}
              key={role.Id}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-label={`Expand ${role.Title}`}
                aria-controls={`${role.Id}-accordion-content`}
                id={`${role.Id}-accordion-header`}
              >
                <RoleControl
                  role={role}
                  isUserPermitted={isUserPermitted && !(role.Id === 'VisitsManager' && accountAdminAssigned)}
                  userTarget={userTarget}
                  initialAssignmentState={targetHasRole(role)}
                  externalAssigned={role.Id === 'VisitsManager' && accountAdminAssigned}
                  onChange={handleRoleChange}
                  onError={() => setHasError(true)}
                />
              </AccordionSummary>
              <AccordionDetails>
                <div>
                  <Typography>{role.Description}</Typography>
                  <ul>
                    {map(role.Details, (detail, idx) => (
                      <li key={idx}>
                        <Typography variant="body2">{detail}</Typography>
                      </li>
                    ))}
                  </ul>
                </div>
              </AccordionDetails>
            </Accordion>
          ))}
        {hasError && (
          <Box mt={2}>
            <Alert
              severity="error"
              icon={false}
              className={classes.alert}
            >
              Error saving role assignment.
            </Alert>
          </Box>
        )}
      </Body>
      {isReviewingNewUser ? (
        <Footer>
          <SecondaryActionButton onClick={onDeleteUser}>Deny</SecondaryActionButton>
          <PrimaryActionButton onClick={onInviteUser}>Approve</PrimaryActionButton>
        </Footer>
      ) : (
        <Footer>
          <PrimaryActionButton onClick={() => onClose(hasChanged)}>Done</PrimaryActionButton>
        </Footer>
      )}
    </NonDismissibleDialog>
  )
}

ManageRolesModal.propTypes = {
  userTarget: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isUserPermitted: RoleControl.propTypes.isUserPermitted,
}

export default ManageRolesModal
